<template>
  <div class="d-flex">
    <div class="action-buttons">
      <!-- ❤️ Favorites -->
      <router-link :to="{ name: 'ManageFavorites' }" class="nav-icon" aria-label="Favorites">
        <i class="mdi mdi-heart"></i>
      </router-link>

      <!-- 💬 Messages -->
      <div class="messages-wrapper" style="position: relative;">
        <button @click.stop="toggleMessagesDropdown" class="nav-icon position-relative messages-nav-icon"
          aria-label="Messages" :aria-expanded="messagesDropdownOpen">
          <i class="mdi mdi-comment"></i>
          <span v-if="unreadMessagesCount > 0" class="unread-badge">
            {{ unreadMessagesCount > 9 ? '9+' : unreadMessagesCount }}
          </span>
        </button>
        <ul v-if="messagesDropdownOpen" class="dropdown-menu-class dropdown-menu-absolute messages-dropdown"
          @click.stop>
          <li class="dropdown-header-msg">
            You have {{ unreadMessagesCount }} unread message(s)
          </li>
          <li class="messages-list">
            <div v-for="message in unreadMessages" :key="message.id" :class="['message-item', 'unread']"
              @click="openMessage(message)">
              <div class="message-header">
                <img :src="message.from_contact ? storageUrl + message.from_contact.picture : userpng"
                  class="message-avatar" alt="User" />
                <div class="message-info">
                  <span class="message-sender">{{ message.from_contact?.username }}</span>
                  <span class="message-text">{{ message.text }}</span>
                  <span class="message-time">
                    <i class="mdi mdi-clock-outline"></i>
                    {{ message.created_at | moment }}
                  </span>
                </div>
              </div>
            </div>
          </li>
          <li v-if="unreadMessagesCount === 0" class="no-messages">
            {{ $t('sentence.NoNewMessages') }}
          </li>
          <li class="dropdown-footer-msg">
            <router-link :to="{ name: 'messenger' }" @click.native="messagesDropdownOpen = false">
              <i class="mdi mdi-message-text"></i> View All Messages
            </router-link>
          </li>
        </ul>
      </div>

      <!-- 🔔 Notifications -->
      <div class="notifications-wrapper" style="position: relative;">
        <button @click.stop="toggleNotificationsDropdown" class="nav-icon position-relative notifications-nav-icon"
          aria-label="Notifications" :aria-expanded="notificationsDropdownOpen">
          <i class="mdi mdi-bell"></i>
          <span v-if="notifications.length > 0" class="unread-badge">
            {{ notifications.length > 9 ? '+9' : notifications.length }}
          </span>
        </button>
        <ul v-if="notificationsDropdownOpen" class="dropdown-menu-class dropdown-menu-absolute notifications-dropdown"
          @click.stop>
          <li class="dropdown-header-notif">
            You have {{ notifications.length }} notification(s)
          </li>
          <li class="notifications-list">
            <div v-for="notification in notifications" :key="notification.id"
              :class="['notification-item', notification.read == 1 ? 'readen' : 'unread']"
              @click="markAsRead(notification.link, notification.id, notification.model_id)">
              <span class="notification-text"><i :class="notification.icons"></i>{{ notification.text }}</span>
              <span class="notification-time">
                <i class="mdi mdi-clock-outline"></i>
                {{ notification.created_at | moment }}
              </span>
            </div>
          </li>
          <li v-if="notifications.length === 0" class="no-notifications">
            {{ $t('sentence.NoNewNotifications') }}
          </li>
        </ul>
      </div>
    </div>

    <!-- Right side (Desktop) -->
    <div class="navbar-buttons">
      <!-- If not logged in -->
      <template v-if="!user || Object.keys(user).length === 0">
        <router-link :to="{ name: 'login' }" class="btn btn-primary">{{ $t('header.signIn') }}</router-link>
      </template>

      <!-- If logged in -->
      <template v-else>
        <div class="dropdown user-menu horizontal-user-menu" style="position: relative;"
          @click.stop="dropdownOpen = !dropdownOpen">
          <button class="dropdown-toggle nav-link">
            <span class="user-name">{{ user.firstname }} {{ user.lastname }}</span>
            <img :src="user.picture ? storageUrl + user.picture : userpng" class="contact-avatar" alt="User Image" />
          </button>

          <ul v-if="dropdownOpen" class="dropdown-menu-class dropdown-menu-absolute" @click.stop>
            <li v-for="item in simpleMenuItems" :key="item.routeName">
              <router-link :to="{ name: item.routeName }">
                <i :class="item.icon"></i> {{ getTranslatedLabel(item) }}
              </router-link>
            </li>
            <li class="divider"></li>
            <li class="dropdown-footer">
              <a @click="logout">
                <i class="mdi mdi-logout"></i> {{ $t('auth.logout') }}
              </a>
            </li>
          </ul>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import config from "@/config";
import userpng from '@/assets/img/user/user.webp';
import helper from '@/services/helper';
import {
  filterByPermissions,
  getMenuItems,
  getVisibleSubItems,
} from '../navigationConfig';

export default {
  name: "ToggleActionButtons",
  data() {
    return {
      userpng: userpng,
      storageUrl: config.storageUrl,
      dropdownOpen: false,
      notificationsDropdownOpen: false,
      messagesDropdownOpen: false,
      closeHandler: null,
    };
  },
  mounted() {
    this.closeHandler=this.closeAllDropdownsOnOutsideClick.bind(this);
    document.addEventListener('click', this.closeHandler);
  },
  methods: {
    toggleNotificationsDropdown() {
      this.notificationsDropdownOpen=!this.notificationsDropdownOpen;
      if (this.dropdownOpen) {
        this.dropdownOpen=false;
      }
      if (this.messagesDropdownOpen) {
        this.messagesDropdownOpen=false;
      }
    },
    toggleMessagesDropdown() {
      this.messagesDropdownOpen=!this.messagesDropdownOpen;
      if (this.dropdownOpen) {
        this.dropdownOpen=false;
      }
      if (this.notificationsDropdownOpen) {
        this.notificationsDropdownOpen=false;
      }
    },
    closeAllDropdownsOnOutsideClick(e) {
      const userMenu=this.$el.querySelector('.user-menu.horizontal-user-menu');
      if (userMenu&&!userMenu.contains(e.target)) {
        this.dropdownOpen=false;
      }

      const notificationIcon=this.$el.querySelector('.notifications-nav-icon');
      const notificationDropdown=this.$el.querySelector('.notifications-dropdown');
      if (notificationIcon&&!notificationIcon.contains(e.target)&&
        notificationDropdown&&!notificationDropdown.contains(e.target)) {
        this.notificationsDropdownOpen=false;
      }

      const messagesIcon=this.$el.querySelector('.messages-nav-icon');
      const messagesDropdown=this.$el.querySelector('.messages-dropdown');
      if (messagesIcon&&!messagesIcon.contains(e.target)&&
        messagesDropdown&&!messagesDropdown.contains(e.target)) {
        this.messagesDropdownOpen=false;
      }
    },
    logout() {
      this.$store.dispatch("logout").then(() => {
        this.$router.push("/login");
      });
    },
    markAsRead(link, id, model_id) {
      this.notificationsDropdownOpen=false;
      var self=this;
      this.$axios({
        url: "notification/markAsRead/"+id,
        method: "POST",
      })
        .then((response) => {
          self.notifications=response.data;


          this.$router.push({ name: link, params: { id: model_id } });
        })
        .catch(function (err) {
          console.log(err);
        });
    },
    openMessage(message) {
      this.messagesDropdownOpen=false;
      this.$router.push({
        name: 'messenger',
        params: { id: message.from, productId: message.product_id }
      });
    },
    getTranslatedLabel: helper.getTranslatedLabel,
  },
  computed: {
    user: {
      get() {
        return this.$store.state.user;
      },
      set(user) {
        this.$store.commit("setUser", { user: user });
      },
    },
    messages: {
      get() {
        return this.$store.state.messages;
      },
      set(messages) {
        this.$store.commit("setMessages", {
          messages: messages,
        });
      },
    },
    unreadMessages() {
      if (!this.messages||this.messages.length===0) {
        return [];
      }

      return this.messages.filter(message => message.read===0);
    },
    unreadMessagesCount() {
      return this.unreadMessages.length;
    },
    notifications: {
      get() {
        return this.$store.state.notifications;
      },
      set(notifications) {
        this.$store.commit("setNotifications", {
          notifications: notifications,
        });
      },
    },
    visibleMenuItems() {
      const items=getMenuItems(this.user);
      return filterByPermissions(items, this.user);
    },
    flattenedMenuItems() {
      const flattened=[];
      this.visibleMenuItems.forEach(item => {
        if (item.hasSubmenu&&item.subItems) {
          const visibleSubItems=getVisibleSubItems(item.subItems, this.user);
          flattened.push(...visibleSubItems);
        } else {
          flattened.push(item);
        }
      });
      return flattened;
    },
    simpleMenuItems() {
      return this.flattenedMenuItems;
    },
  },
  beforeDestroy() {
    if (this.closeHandler) {
      document.removeEventListener('click', this.closeHandler);
      this.closeHandler=null;
    }
  },
};
</script>

<style scoped>
.navbar-buttons {
  display: none;
  gap: 12px;
  flex-shrink: 0;
}

.user-menu.horizontal-user-menu {
  display: flex;
  align-items: center;
  margin-bottom: 0;
  gap: 10px;
}

.dropdown-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 100%;
  font-size: 15px;
  color: var(--color-3);
  font-weight: 600;
}

.dropdown-menu-class {
  list-style: none;
  padding: 8px 0;
  margin: 8px 0 0 0;
  background-color: var(--light);
  border: 1px solid var(--light);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 320px;
  overflow-y: auto;
  min-width: 220px;
  min-height: 433px;
}

.dropdown-menu-absolute {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 100;
  min-height: 444px;
}

.dropdown-menu-class li {
  padding: 0;
}

.dropdown-menu-class li a,
.dropdown-menu-class li router-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  color: var(--color-6);
  text-decoration: none;
  transition: all 0.2s ease;
  font-size: 14px;
}

.dropdown-menu-class li router-link:hover {
  background-color: var(--gray-200);
  color: var(--color-nz);
}

.dropdown-footer {
  border-top: 1px solid var(--color-d);
}

.divider {
  height: 1px;
  background-color: var(--color-d);
  margin: 4px 0;
}

.nav-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  text-decoration: none;
  transition: all 0.2s ease;
}

.nav-icon i {
  color: var(--color-6);
  font-size: 20px;
  transition: color 0.2s ease;
}

.nav-icon:hover {
  transform: scale(1.1);
}

.unread-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
}

@keyframes pulse {

  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 2px 6px rgba(255, 71, 87, 0.4);
  }

  50% {
    transform: scale(1.08);
    box-shadow: 0 3px 10px rgba(255, 71, 87, 0.6);
  }
}

@keyframes bounce {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-3px);
  }
}

.unread-badge {
  animation: bounce 1s infinite;
}

/* Messages Dropdown Styles */
.messages-wrapper {
  position: relative;
  display: inline-block;
}

.messages-nav-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.messages-nav-icon i {
  font-size: 20px;
  transition: color 0.2s ease;
}

.messages-nav-icon:hover {
  color: var(--color-nz);
}

.messages-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border: 1px solid var(--light);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  min-width: 360px;
  max-width: 400px;
  max-height: 520px;
  overflow: hidden;
  z-index: 100;
  animation: slideDown 0.2s ease;
}

.dropdown-header-msg {
  padding: 16px;
  background: linear-gradient(135deg, var(--light) 0%, white 100%);
  border-bottom: 1px solid var(--light);
  font-weight: 600;
  font-size: 15px;
  color: var(--color-3);
  position: sticky;
  top: 0;
  z-index: 10;
}

.messages-list {
  max-height: 400px;
  overflow-y: auto;
  padding: 0;
  margin: 0;
  list-style: none;
}

.message-item {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--light);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.message-item:last-child {
  border-bottom: none;
}

.message-item.unread {
  background: linear-gradient(90deg, var(--primary-light) 0%, white 100%);
  border-left: 3px solid var(--color-nz);
}

.message-item:hover {
  background: var(--light);
  transform: translateX(2px);
}

.message-item.unread:hover {
  background: linear-gradient(90deg, var(--primary-light) 0%, var(--light) 100%);
}

.message-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.message-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 2px solid var(--light);
}

.message-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.message-sender {
  font-weight: 600;
  font-size: 14px;
  color: var(--color-3);
}

.message-time {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--color-9);
}

.message-time i {
  font-size: 12px;
  color: var(--color-9);
}

.message-text {
  font-size: 13px;
  line-height: 1.5;
  color: var(--color-6);
  word-wrap: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.no-notifications,
.no-messages {
  padding: 40px 20px !important;
  text-align: center;
  color: var(--color-9);
  font-size: 14px;
  font-style: italic;
}

.dropdown-footer-msg {
  padding: 12px 16px;
  background: var(--light);
  border-top: 1px solid var(--light);
  position: sticky;
  bottom: 0;
}

.dropdown-footer-msg a {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px;
  color: var(--color-nz);
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.dropdown-footer-msg a:hover {
  background: var(--primary-light);
  color: var(--color-nz-200);
}

.dropdown-footer-msg a i {
  font-size: 18px;
}

.messages-list::-webkit-scrollbar {
  width: 6px;
}

.messages-list::-webkit-scrollbar-track {
  background: var(--light);
}

.messages-list::-webkit-scrollbar-thumb {
  background: var(--gray-300);
  border-radius: 3px;
}

.messages-list::-webkit-scrollbar-thumb:hover {
  background: var(--gray-400);
}

/* Notifications Dropdown Styles */
.notifications-wrapper {
  position: relative;
  display: inline-block;
}

.notifications-nav-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.notifications-nav-icon i {
  font-size: 20px;
  transition: color 0.2s ease;
}

.notifications-nav-icon:hover {
  color: var(--color-nz);
}

.notifications-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border: 1px solid var(--light);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  min-width: 320px;
  max-width: 380px;
  max-height: 480px;
  overflow: hidden;
  z-index: 100;
  animation: slideDown 0.2s ease;
}

.dropdown-header-notif {
  padding: 16px;
  background: linear-gradient(135deg, var(--light) 0%, white 100%);
  border-bottom: 1px solid var(--light);
  font-weight: 600;
  font-size: 15px;
  color: var(--color-3);
  position: sticky;
  top: 0;
  z-index: 10;
}

.notifications-list {
  max-height: 400px;
  overflow-y: auto;
  padding: 0;
  margin: 0;
  list-style: none;
}

.notification-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--light);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-item.unread {
  background: linear-gradient(90deg, var(--primary-light) 0%, white 100%);
  border-left: 3px solid var(--color-nz);
}

.notification-item.readen {
  background: white;
  opacity: 0.85;
}

.notification-item:hover {
  background: var(--light);
  transform: translateX(2px);
}

.notification-item.unread:hover {
  background: linear-gradient(90deg, var(--primary-light) 0%, var(--light) 100%);
}

.notification-item i {
  font-size: 18px;
  color: var(--color-nz);
  margin-right: 8px;
  flex-shrink: 0;
}

.notification-text {
  flex: 1;
  font-size: 14px;
  line-height: 1.5;
  color: var(--color-3);
  word-wrap: break-word;
}

.notification-time {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--color-9);
  margin-top: 4px;
}

.notification-time i {
  font-size: 12px;
  color: var(--color-9);
}

.notifications-list::-webkit-scrollbar {
  width: 6px;
}

.notifications-list::-webkit-scrollbar-track {
  background: var(--light);
}

.notifications-list::-webkit-scrollbar-thumb {
  background: var(--gray-300);
  border-radius: 3px;
}

.notifications-list::-webkit-scrollbar-thumb:hover {
  background: var(--gray-400);
}

.notifications-nav-icon:focus {
  outline: 2px solid var(--color-nz);
  outline-offset: 2px;
}

.notification-item:focus {
  outline: 2px solid var(--color-nz);
  outline-offset: -2px;
}

@media (hover: none) and (pointer: coarse) {

  .notification-item,
  .message-item {
    -webkit-tap-highlight-color: rgba(33, 150, 243, 0.1);
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (min-width: 767px) {
  .action-buttons {
    margin-inline-end: 1rem;
  }

  .nav-icon {
    padding: 10px;
  }

  .nav-icon i {
    font-size: 22px;
  }

  .navbar-buttons {
    display: flex;
  }
}

@media (max-width: 767px) {

  .notifications-dropdown,
  .messages-dropdown {
    position: fixed;
    top: auto;
    right: 0;
    left: 0;
    top: 70px;
    min-width: 100%;
    max-width: 100%;
    max-height: 70vh;
    border-radius: 16px 16px 0 0;
    box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.2);
    animation: slideDown 0.3s ease;
  }

  @keyframes slideDown {
    from {
      transform: translateY(100%);
    }

    to {
      transform: translateY(0);
    }
  }

  .notifications-dropdown::before {
    content: '';
    position: absolute;
    top: 8px;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 4px;
    background: var(--color-d);
    border-radius: 2px;
    z-index: 20;
  }

  .dropdown-header-notif {
    padding: 24px 16px 16px;
    text-align: center;
    font-size: 16px;
  }

  .notification-item,
  .message-item {
    padding: 16px;
  }

  .notification-item i {
    font-size: 20px;
  }

  .notification-text,
  .message-text {
    font-size: 15px;
    line-height: 1.6;
  }

  .message-avatar {
    width: 44px;
    height: 44px;
  }

  .message-sender {
    font-size: 15px;
  }

  .notification-item:active,
  .message-item:active {
    background: var(--gray-200);
  }

  .notification-item.unread:active,
  .message-item.unread:active {
    background: linear-gradient(90deg, var(--primary-light) 0%, var(--gray-200) 100%);
  }

  .notifications-list,
  .messages-list {
    max-height: calc(70vh - 140px);
  }

  .no-notifications,
  .no-messages {
    padding: 60px 20px;
    font-size: 15px;
  }
}

@media (max-width: 767px) {
  .notifications-wrapper.open::after {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 100;
    animation: fadeIn 0.3s ease;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }
}

@media (max-width: 375px) {
  .notifications-dropdown {
    max-height: 75vh;
  }

  .dropdown-header-notif {
    padding: 20px 12px 12px;
    font-size: 15px;
  }

  .notification-item {
    padding: 14px 12px;
  }

  .notification-text {
    font-size: 14px;
  }

  .notifications-list {
    max-height: calc(75vh - 70px);
  }
}
</style>